import { useState, useEffect, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { TextButton, EmptyState } from '@/lib';
import { orgRoute } from '@/app/org-route';
import {
  listSessions,
  saveSession,
  deleteSession,
  type SavedSession,
} from './storage';

export type S5bSessionsProps = {
  /** Optional seed sessions for tests + storybook. */
  initial?: SavedSession[];
};

/**
 * S5b Sessions. Lists saved analysis-workspace snapshots from
 * local storage. "Open" routes to /analyze with the saved t /
 * sensors / baseline / pins re-applied as search params.
 *
 * Saving new sessions happens from S5's "Save session" button
 * (currently a stub — wiring it up is a follow-up). For now this
 * screen exposes a quick "Save example session" affordance so
 * the empty-state has a self-service way to populate.
 */
export function S5bSessions({ initial }: S5bSessionsProps) {
  const [sessions, setSessions] = useState<SavedSession[]>(initial ?? []);

  const refresh = useCallback(() => {
    setSessions(listSessions());
  }, []);

  useEffect(() => {
    if (initial) return;
    refresh();
  }, [initial, refresh]);

  if (sessions.length === 0) {
    return (
      <EmptyState
        variant="empty"
        title="No saved sessions yet"
        body="Saved sessions capture an S5 workspace — selected sensors, cursor time, baseline, and pins — so you can recall a diagnosis exactly as you left it."
        cta={{
          label: 'Save an example session',
          onClick: () => {
            saveSession({
              name: 'Cooling pump 204 · BPFI investigation',
              assetId: 'asset_1',
              sensors: ['sensor_1'],
              baseline: 'baseline_2026_06_18',
              t: Date.now(),
              pins: [],
            });
            refresh();
          },
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Signal analysis
          </p>
          <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">
            Saved sessions
          </h1>
          <p className="mt-sp-1 text-fs-meta text-gray-700">
            {sessions.length} session{sessions.length === 1 ? '' : 's'}
          </p>
        </div>
        <Link
          to={orgRoute('/analyze')}
          className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-1 text-fs-meta text-gray-900 hover:border-gray-500"
        >
          Open workspace ↗
        </Link>
      </header>

      <ul className="flex flex-col gap-sp-2">
        {sessions.map((s) => (
          <SessionRow key={s.id} session={s} onDelete={refresh} />
        ))}
      </ul>
    </div>
  );
}

function SessionRow({
  session,
  onDelete,
}: {
  session: SavedSession;
  onDelete: () => void;
}) {
  const created = new Date(session.createdAt);
  return (
    <li className="grid grid-cols-[1fr_auto_auto] items-center gap-sp-3 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2">
      <div className="min-w-0">
        <p className="truncate text-fs-body font-medium text-gray-900">
          {session.name}
        </p>
        <p className="truncate text-fs-meta text-gray-700">
          {session.assetId ? <span className="font-mono">{session.assetId}</span> : '—'}
          {session.sensors?.length
            ? ` · ${session.sensors.length} sensor${session.sensors.length === 1 ? '' : 's'}`
            : ''}
          {session.baseline ? (
            <>
              {' · baseline '}
              <span className="font-mono">{session.baseline}</span>
            </>
          ) : null}
          {' · '}
          <time dateTime={session.createdAt}>{created.toLocaleString()}</time>
        </p>
      </div>
      <Link
        to={orgRoute('/analyze')}
        search={{
          sensors: session.sensors,
          baseline: session.baseline,
          t: session.t,
          pins: session.pins,
        }}
        className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-1 text-fs-meta text-gray-900 hover:border-gray-500"
      >
        Open
      </Link>
      <TextButton
        label="Delete"
        onClick={() => {
          if (window.confirm(`Delete session "${session.name}"?`)) {
            deleteSession(session.id);
            onDelete();
          }
        }}
      />
    </li>
  );
}

// Re-export the save helper so S5's eventual "Save session" wiring
// can reach it through this screen module without poking at the
// storage details directly.
export { saveSession } from './storage';
